import useRouter from "common/hooks/use-router";
import useItem from "hooks/useItem";
import useList from "hooks/useList";
import useAuth from "hooks/useAuth";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Organization, Subscription, User } from "types";
import PageLoading from "common/components/PageLoading/PageLoading";
import DetailHeader from "common/components/DetailHeader/DetailHeader";
import ImageComponent from "common/components/ImageComponent/ImageComponent";
import ImageFromStrapiMedia from "common/components/ImageFromStrapiMedia/ImageFromStrapiMedia";
import Icon, { IconNames } from "components/Icon/Icon";
import { TbGauge } from "react-icons/tb";
import CrudList from "common/components/Crud/CrudList";
import CrudEdit from "common/components/Crud/CrudEdit";
import type { TableColumn } from "common/components/DataTable/DataTable";
import type { CrudHeaderAction } from "common/components/Crud/components/CrudHeader";
import { ProductNameLabel } from "pages/Products/productUtils";
import OrganizationUserForm from "./OrganizationUserForm";

const SUBSCRIPTION_ICONS: IconNames[] = ["BsStar", "BsStarHalf", "BsStarFill"];
const SECONDARY_COLOR = "#009FED";
const TERNARY_COLOR = "#98CC33";

const interpolateColor = (t: number) => {
  const r = Math.round(0x00 + t * (0x98 - 0x00));
  const g = Math.round(0x9f + t * (0xcc - 0x9f));
  const b = Math.round(0xed + t * (0x33 - 0xed));
  return `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};

function SidebarUserForm({
  organizationId,
  organizationSubscriptionId,
  organizationPlansIds,
  ...props
}: {
  organizationId: number;
  organizationSubscriptionId?: number;
  organizationPlansIds?: number[];
  [key: string]: any;
}) {
  return (
    <CrudEdit
      collectionName="users"
      Form={OrganizationUserForm}
      organizationId={organizationId}
      organizationPlansIds={organizationPlansIds}
      organizationSubscriptionId={organizationSubscriptionId}
      {...props}
    />
  );
}

function OrganizationDetail() {
  const router = useRouter();
  const { t } = useTranslation();
  const id = router.query.id as string;

  const { user } = useAuth();

  const { item, isFetching } = useItem<Organization>(
    "organizations",
    id ?? user?.organization?.id,
  );
  const { items: subscriptions } = useList<Subscription>("subscriptions");

  const subscriptionMeta = useMemo(() => {
    const list = subscriptions ?? [];
    return list.reduce((acc, s, index) => {
      const ratio = list.length > 1 ? index / (list.length - 1) : 0;
      const color =
        index === 0
          ? SECONDARY_COLOR
          : index === list.length - 1
          ? TERNARY_COLOR
          : interpolateColor(ratio);
      acc[s.id] = { icon: SUBSCRIPTION_ICONS[index] ?? "BsStarFill", color };
      return acc;
    }, {} as Record<number, { icon: IconNames; color: string }>);
  }, [subscriptions]);

  const userColumns: TableColumn<User>[] = useMemo(
    () => [
      {
        field: "username",
        header: t("users.username"),
        sortable: true,
        customRender: (row: User) => (
          <span className="font-bold">{row.username}</span>
        ),
      },
      {
        field: "email",
        header: t("users.email"),
        sortable: true,
        customRender: (row: User) => (
          <span>
            {row.email?.includes("fakemail.com") ? "-" : row.email || "-"}
          </span>
        ),
      },
    ],
    [t],
  );

  const userActions: CrudHeaderAction[] = useMemo(
    () => [
      {
        title: t("common.new"),
        icon: "PlusCircleIconSolid",
        action: "openSidebar",
        type: "white",
        className: "flex flex-row-reverse",
      },
    ],
    [t],
  );

  if (isFetching && !item) return <PageLoading />;

  const org = item as Organization;
  const sub = org?.subscription;
  const subMeta = sub ? subscriptionMeta[sub.id] : null;

  return (
    <div className="flex flex-col h-full p-2 px-4 gap-6">
      <DetailHeader
        title={org?.name ?? "-"}
        backLink="/organizations"
        TitleIcon={
          org?.logo ? (
            <div className="rounded-lg overflow-hidden w-full h-full object-cover shadow-md">
              <ImageComponent image={ImageFromStrapiMedia(org.logo).uri} />
            </div>
          ) : undefined
        }
      />

      <div className="flex flex-1 gap-4overflow-hidden">
        {/* Bloc détail organisation */}
        <div className="flex flex-col gap-4 shrink-0">
          {sub && subMeta && (
            <div className="white-box flex flex-col gap-3">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                {t("organizations.subscription")}
              </span>
              <div
                className="inline-flex items-center gap-2 rounded-lg px-3 py-2 w-fit"
                style={{ backgroundColor: subMeta.color }}
              >
                <Icon name={subMeta.icon} className="w-4 h-4 text-white" />
                <span className="text-sm font-semibold text-white">
                  {sub.name}
                </span>
              </div>
              {sub.description && (
                <p className="text-xs text-slate-500">{sub.description}</p>
              )}
            </div>
          )}

          {org?.products && org.products.length > 0 && (
            <div className="white-box flex flex-col gap-3">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                {t("organizations.products")}
              </span>
              <div className="flex flex-col gap-2">
                {org.products.map((p) => (
                  <div key={p.id} className="flex items-center gap-2">
                    {p.image?.url ? (
                      <img
                        src={ImageFromStrapiMedia(p.image).uri}
                        alt={p.name}
                        className="w-8 h-8 rounded object-cover shrink-0"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded bg-slate-200 shrink-0" />
                    )}
                    <ProductNameLabel key={p.id} product={p} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {org?.plans && org.plans.length > 0 && (
            <div className="white-box flex flex-col gap-3">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                {t("organizations.plans")}
              </span>
              <div className="flex flex-col gap-2">
                {org.plans.map((p) => (
                  <div key={p.id} className="flex items-center gap-2">
                    <TbGauge className="text-primary-800 size-7 rounded object-cover shrink-0" />
                    <span>{p.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Bloc utilisateurs */}
        <div className="flex flex-col w-full pl-4">
          <CrudList
            collectionName="users"
            columns={userColumns}
            headerActions={userActions}
            defaultFilters={{ organization: id, onlyPublicUsers: true }}
            defaultSort="username:ASC"
            canDelete
            canEdit
            formMode="sidebar"
            SidebarFormComponent={(props: any) => (
              <SidebarUserForm
                organizationId={Number(id)}
                organizationSubscriptionId={org?.subscription?.id}
                organizationPlansIds={org?.plans?.map((p) => p.id)}
                {...props}
              />
            )}
            actionsHeader=""
            className="flex-1 w-full"
            nameSpace="public-users"
          />
        </div>
      </div>
    </div>
  );
}

export default OrganizationDetail;
